import type {Vector3} from "@cm/math"
import {Three as THREE} from "@cm/material-nodes/three"
import {PriorityQueue} from "@cm/utils"

export const removeDuplicates = (controlPoints: Vector3[]) => {
    const newControlPoints: Vector3[] = []
    for (let i = 0; i < controlPoints.length; i++) {
        if (i === 0 || !controlPoints[i].equals(controlPoints[i - 1])) {
            newControlPoints.push(controlPoints[i])
        }
    }
    return newControlPoints
}

export const densifyControlPoints = (controlPoints: Vector3[], maxDistance: number) => {
    const newControlPoints: Vector3[] = []
    newControlPoints.push(controlPoints[0])
    for (let i = 0; i < controlPoints.length - 1; i++) {
        const start = controlPoints[i]
        const end = controlPoints[i + 1]
        const distance = start.distance(end)

        if (distance > maxDistance) {
            const numSegments = Math.ceil(distance / maxDistance)
            for (let j = 1; j <= numSegments; j++) {
                const t = j / numSegments
                newControlPoints.push(start.add(end.sub(start).mul(t)))
            }
        } else {
            newControlPoints.push(end)
        }
    }

    return newControlPoints
}

const samplesPerSegment = 24

const toThree = ({x, y, z}: Vector3) => new THREE.Vector3(x, y, z)

const segment = new THREE.Line3()
const closest = new THREE.Vector3()
const distanceToSegment = (point: THREE.Vector3, polyline: THREE.Vector3[], index: number) =>
    point.distanceTo(segment.set(polyline[index]!, polyline[index + 1]!).closestPointToPoint(point, true, closest))

// the points and the curve run the same way, so each point's nearest stretch is found by walking on from the previous
// one's; that walk can stop at a stretch that is only locally nearest, so a point is only given up after a full search
const allWithin = (points: THREE.Vector3[], polyline: THREE.Vector3[], maxError: number) => {
    const segmentCount = polyline.length - 1
    let cursor = 0
    for (const point of points) {
        let nearest = distanceToSegment(point, polyline, cursor)
        for (let next = cursor + 1; next < segmentCount; next++) {
            const distance = distanceToSegment(point, polyline, next)
            if (distance > nearest) break
            nearest = distance
            cursor = next
        }
        if (nearest > maxError) {
            for (let index = 0; index < segmentCount; index++) nearest = Math.min(nearest, distanceToSegment(point, polyline, index))
            if (nearest > maxError) return false
        }
    }
    return true
}

// removes the straightest points first, as long as every point it was given stays within maxError of the centripetal
// Catmull-Rom through what is left, which is how the platform draws a curve
export const simplifyControlPoints = (controlPoints: Vector3[], maxError: number) => {
    const getPointStraightness = (controlPoints: Vector3[], index: number) => {
        const prev = controlPoints[index - 1]
        const current = controlPoints[index]
        const next = controlPoints[index + 1]

        const v1 = current.sub(prev).normalized()
        const v2 = next.sub(current).normalized()

        return v1.dot(v2)
    }

    const originals = controlPoints.map(toThree)
    const originalIndex = new Map(controlPoints.map((point, index) => [point, index]))

    // a Catmull-Rom segment depends on the two points to either side, so a removal changes the curve two points each way
    const staysWithinTolerance = (current: Vector3[], index: number) => {
        const from = Math.max(0, index - 2)
        const to = Math.min(current.length - 1, index + 2)
        const windowStart = Math.max(0, from - 1)
        const window = current.slice(windowStart, Math.min(current.length, to + 2)).filter((_, i) => windowStart + i !== index)
        const curve = new THREE.CatmullRomCurve3(window.map(toThree), false, "centripetal")
        const segmentCount = window.length - 1

        const polyline: THREE.Vector3[] = []
        for (let segment = from - windowStart; segment < to - windowStart - 1; segment++) {
            for (let step = 0; step <= samplesPerSegment; step++) polyline.push(curve.getPoint((segment + step / samplesPerSegment) / segmentCount))
        }

        return allWithin(originals.slice(originalIndex.get(current[from]!)! + 1, originalIndex.get(current[to]!)!), polyline, maxError)
    }

    const priorityQueue = new PriorityQueue<Vector3>()
    for (let i = 1; i < controlPoints.length - 1; i++) {
        priorityQueue.enqueue(controlPoints[i], getPointStraightness(controlPoints, i))
    }

    const currentControlPoints = [...controlPoints]

    while ((priorityQueue.peekPriority() ?? -Infinity) !== -Infinity) {
        const point = priorityQueue.dequeue()
        if (!point) break

        const index = currentControlPoints.indexOf(point)
        if (index > 0 && index < currentControlPoints.length - 1 && staysWithinTolerance(currentControlPoints, index)) {
            currentControlPoints.splice(index, 1)
            if (index > 1) priorityQueue.updatePriority(currentControlPoints[index - 1], getPointStraightness(currentControlPoints, index - 1))
            if (index < currentControlPoints.length - 1)
                priorityQueue.updatePriority(currentControlPoints[index], getPointStraightness(currentControlPoints, index))
        } else {
            priorityQueue.enqueue(point, -Infinity)
        }
    }

    return currentControlPoints
}
